import {
    useCallback,
    useContext,
    useEffect,
    useState
} from "react";
import {
    useWindowDimensions,
    ActivityIndicator,
    TouchableOpacity,
    ScrollView,
    View
} from "react-native";
import type {
    TeacherItem
} from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitDialog,
    NCoreUIKitTheme,
    NCoreUIKitToast,
    PageContainer,
    StateCard,
    TextInput,
    Button,
    Modal,
    Text
} from "ncore-ui-kit";
import {
    ChevronRightIcon,
    ChevronLeftIcon,
    RefreshCcwIcon,
    PencilIcon,
    TrashIcon,
    UsersIcon
} from "lucide-react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import {
    AuthContext
} from "../../contexts/AuthContext";

const Teachers = () => {
    const {
        radiuses,
        spaces,
        colors
    } = NCoreUIKitTheme.useContext();

    const {
        activeOrganizationId,
        user
    } = useContext(AuthContext);

    const {
        width
    } = useWindowDimensions();
    const isMobile = width < 768;

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        teachers,
        setTeachers
    ] = useState<TeacherItem[]>([]);

    const [
        isRefreshing,
        setIsRefreshing
    ] = useState<boolean>(false);

    const [
        modalVisible,
        setModalVisible
    ] = useState(false);

    const [
        editId,
        setEditId
    ] = useState<string | null>(null);

    const [
        formData,
        setFormData
    ] = useState({
        name_surname: "",
        school_name: "",
        grade_year: "",
        address: "",
        email: "",
        notes: "",
        phone: "",
        tc_no: "",
        age: ""
    });

    const [
        isSubmitting,
        setIsSubmitting
    ] = useState(false);

    const [
        isLoading,
        setIsLoading
    ] = useState(false);

    const [
        searchQuery,
        setSearchQuery
    ] = useState("");

    const [
        debouncedSearch,
        setDebouncedSearch
    ] = useState("");

    const [
        previewItem,
        setPreviewItem
    ] = useState<TeacherItem | null>(null);

    const [
        pagination,
        setPagination
    ] = useState({
        totalRecords: 0,
        currentPage: 1,
        totalPages: 1,
        limit: 10
    });

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchQuery);
            setPagination(prev => ({
                ...prev,
                currentPage: 1
            }));
        }, 500);

        return () => clearTimeout(handler);
    }, [searchQuery]);

    const fetchTeachers = useCallback(async (page: number = 1) => {
        try {
            setIsLoading(true);
            const token = await AsyncStorage.getItem("authToken");
            const url = new URL(`${process.env.API_URL}/teachers`);
            url.searchParams.append("page", page.toString());
            url.searchParams.append("limit", pagination.limit.toString());

            if (debouncedSearch) {
                url.searchParams.append("search", debouncedSearch);
            }

            if (activeOrganizationId && user?.role === "root") {
                url.searchParams.append("organization_id", activeOrganizationId);
            }

            const response = await fetch(url.toString(), {
                headers: {
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                }
            });
            const result = await response.json();
            if (result.status === "success") {
                setTeachers(result.data);
                if (result.meta) {
                    setPagination(result.meta);
                }
            }
        } catch (error) {
            console.error("Hocalar alınamadı:", error);
        } finally {
            setIsLoading(false);
        }
    }, [
        activeOrganizationId,
        debouncedSearch,
        user?.role,
        pagination.limit
    ]);

    useEffect(() => {
        if (user?.role === "root" && !activeOrganizationId) {
            setTeachers([]);
            setPagination({
                totalRecords: 0,
                currentPage: 1,
                totalPages: 1,
                limit: 10
            });
            return;
        }

        fetchTeachers(pagination.currentPage);
    }, [
        activeOrganizationId,
        fetchTeachers,
        pagination.currentPage,
        user?.role
    ]);

    const handleSave = async () => {
        if (!formData.name_surname) {
            NCoreUIKitToast.open({
                title: localize("teachers-alert-name-required"),
                type: "danger"
            });
            return;
        }

        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            const token = await AsyncStorage.getItem("authToken");
            const url = editId
                ? `${process.env.API_URL}/teachers/${editId}`
                : `${process.env.API_URL}/teachers`;

            const response = await fetch(url, {
                body: JSON.stringify({
                    ...formData,
                    ...(activeOrganizationId && user?.role === "root" ? {
                        organization_id: activeOrganizationId
                    } : {})
                }),
                headers: {
                    "Content-Type": "application/json",
                    ...(activeOrganizationId && user?.role === "root" ? {
                        "x-organization-id": activeOrganizationId
                    } : {}),
                    Authorization: `Bearer ${token}`
                },
                method: editId ? "PUT" : "POST"
            });

            const result = await response.json();
            if (result.status === "success") {
                setModalVisible(false);
                setEditId(null);
                setFormData({
                    name_surname: "",
                    school_name: "",
                    grade_year: "",
                    address: "",
                    email: "",
                    notes: "",
                    phone: "",
                    tc_no: "",
                    age: ""
                });
                fetchTeachers(1);
            } else {
                NCoreUIKitToast.open({
                    title: result.message || localize("teachers-toast-unknown-error"),
                    type: "danger"
                });
            }
        } catch (error) {
            console.error("Kayıt hatası:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (id: string, name: string) => {
        let isDeleting = false;

        NCoreUIKitDialog.open({
            primaryButtonProps: {
                onPress: async ({
                    closeAnimation
                }) => {
                    if (isDeleting) return;

                    isDeleting = true;

                    try {
                        const token = await AsyncStorage.getItem("authToken");
                        const response = await fetch(`${process.env.API_URL}/teachers/${id}`, {
                            headers: {
                                ...(activeOrganizationId && user?.role === "root" ? {
                                    "x-organization-id": activeOrganizationId
                                } : {}),
                                Authorization: `Bearer ${token}`
                            },
                            method: "DELETE"
                        });

                        const result = await response.json();
                        if (result.status === "success") {
                            fetchTeachers(pagination.currentPage);
                        } else {
                            NCoreUIKitToast.open({
                                title: result.message || localize("teachers-toast-delete-failed"),
                                type: "danger"
                            });
                        }
                    } catch (error) {
                        console.error("Silme hatası:", error);
                    } finally {
                        isDeleting = false;

                        closeAnimation();
                    }
                },
                title: localize("teachers-dialog-delete-primary"),
                type: "danger"
            },
            secondaryButtonProps: {
                title: localize("teachers-dialog-delete-secondary"),
                onPress: ({
                    closeAnimation
                }) => closeAnimation(),
                type: "neutral"
            },
            content: localize("teachers-dialog-delete-content", [
                name
            ]),
            title: localize("teachers-dialog-delete-title"),
            variant: "yes-no",
            isVisible: true
        });
    };

    const handlePageChange = (newPage: number) => {
        if (newPage >= 1 && newPage <= pagination.totalPages) {
            setPagination(prev => ({
                ...prev,
                currentPage: newPage
            }));
        }
    };

    return <PageContainer
        isScrollable={true}
    >
        <View
            style={[
                stylesheet.container,
                {
                    padding: spaces.spacingXl
                }
            ]}
        >
            <View
                style={[
                    stylesheet.headerRow,
                    {
                        alignItems: isMobile ? "stretch" : "center",
                        flexDirection: isMobile ? "column" : "row",
                        gap: isMobile ? spaces.spacingMd : 0,
                        marginBottom: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("teachers-page-title")}
                </Text>
                <View
                    style={{
                        flexDirection: isMobile ? "column" : "row",
                        gap: spaces.spacingMd
                    }}
                >
                    <Button
                        icon={({
                            color,
                            size
                        }) => {
                            return <RefreshCcwIcon
                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                size={size}
                            />;
                        }}
                        onPress={async () => {
                            setIsRefreshing(true);
                            await fetchTeachers(pagination.currentPage);
                            setIsRefreshing(false);
                        }}
                        isLoading={isRefreshing}
                        spreadBehaviour="free"
                        variant="ghost"
                        type="neutral"
                    />
                    <Button
                        spreadBehaviour={isMobile ? "stretch" : "free"}
                        title={localize("teachers-page-add-button")}
                        onPress={() => {
                            setEditId(null);
                            setFormData({
                                name_surname: "",
                                school_name: "",
                                grade_year: "",
                                address: "",
                                email: "",
                                notes: "",
                                phone: "",
                                tc_no: "",
                                age: ""
                            });
                            setModalVisible(true);
                        }}
                        type="primary"
                    />
                </View>
            </View>
            <TextInput
                placeholder={localize("teachers-search-placeholder")}
                title={localize("teachers-search-placeholder")}
                style={{
                    marginBottom: spaces.spacingXl
                }}
                onChangeText={setSearchQuery}
                spreadBehaviour="stretch"
                value={searchQuery}
            />
            {
                isLoading && teachers.length === 0 ?
                    <ActivityIndicator
                        color={colors.content.icon.default}
                        size="large"
                    />
                :
                    teachers.length === 0 ? (
                        <StateCard
                            subTitle={localize("teachers-list-empty-subtitle")}
                            title={localize("teachers-list-empty-title")}
                        />) : (
                            <ScrollView
                                contentContainerStyle={[
                                    stylesheet.listContent,
                                    {
                                        paddingBottom: spaces.spacingXl,
                                        gap: spaces.spacingMd
                                    }
                                ]}
                                showsVerticalScrollIndicator={false}
                            >
                                {teachers.map((item) => <TouchableOpacity
                                    style={[
                                        stylesheet.itemContainer,
                                        {
                                            backgroundColor: colors.content.container.mid,
                                            alignItems: isMobile ? "stretch" : "center",
                                            flexDirection: isMobile ? "column" : "row",
                                            borderRadius: radiuses.md,
                                            padding: spaces.spacingMd,
                                            gap: spaces.spacingMd
                                        }
                                    ]}
                                    onPress={() => setPreviewItem(item)}
                                    activeOpacity={0.7}
                                    key={item.id}
                                >
                                    <View
                                        style={[
                                            stylesheet.itemContent,
                                            {
                                                gap: spaces.spacingMd
                                            }
                                        ]}
                                    >
                                        <View
                                            style={[
                                                stylesheet.itemIcon,
                                                {
                                                    backgroundColor: colors.content.container.default,
                                                    borderRadius: radiuses.full
                                                }
                                            ]}
                                        >
                                            <UsersIcon
                                                color={colors.content.icon.emphasized}
                                                size={24}
                                            />
                                        </View>
                                        <View
                                            style={[
                                                stylesheet.itemInfo,
                                                {
                                                    gap: spaces.spacingSm
                                                }
                                            ]}
                                        >
                                            <Text
                                                variant="titleMediumSize"
                                            >
                                                {item.name_surname}
                                            </Text>
                                            {(item.grade_year || item.phone) && (
                                                <Text
                                                    variant="bodyMediumSize"
                                                    color="mid"
                                                >
                                                    {[
                                                        item.grade_year,
                                                        item.phone
                                                    ].filter(Boolean).join(" • ")}
                                                </Text>)}
                                        </View>
                                    </View>
                                    <View
                                        style={[
                                            stylesheet.itemActions,
                                            {
                                                alignItems: isMobile ? "stretch" : "center",
                                                flexDirection: isMobile ? "column" : "row",
                                                gap: spaces.spacingMd
                                            }
                                        ]}
                                    >
                                        <Button
                                            icon={({
                                                color,
                                                size
                                            }) => <PencilIcon
                                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                size={size}
                                            />
                                            }
                                            spreadBehaviour={isMobile ? "stretch" : "free"}
                                            onPress={() => {
                                                setEditId(item.id);
                                                setFormData({
                                                    name_surname: item.name_surname || "",
                                                    school_name: item.school_name || "",
                                                    grade_year: item.grade_year || "",
                                                    address: item.address || "",
                                                    email: item.email || "",
                                                    notes: item.notes || "",
                                                    phone: item.phone || "",
                                                    tc_no: item.tc_no || "",
                                                    age: item.age || ""
                                                });
                                                setModalVisible(true);
                                            }}
                                            title={localize("teachers-list-edit-button")}
                                            type="neutral"
                                        />
                                        <Button
                                            icon={({
                                                color,
                                                size
                                            }) => <TrashIcon
                                                color={colors.content.icon[color as keyof typeof colors.content.icon]}
                                                size={size}
                                            />
                                            }
                                            onPress={() => handleDelete(item.id, item.name_surname)}
                                            title={localize("teachers-list-delete-button")}
                                            spreadBehaviour={isMobile ? "stretch" : "free"}
                                            type="danger"
                                        />
                                    </View>
                                </TouchableOpacity>)}
                            </ScrollView>)
            }
            {pagination.totalPages > 1 && (
                <View
                    style={[
                        stylesheet.paginationContainer,
                        {
                            marginBottom: spaces.spacingXl,
                            marginTop: spaces.spacingXl,
                            gap: spaces.spacingMd,
                            flexWrap: "wrap"
                        }
                    ]}
                >
                    <Button
                        onPress={() => handlePageChange(pagination.currentPage - 1)}
                        title={localize("teachers-pagination-prev")}
                        isDisabled={pagination.currentPage === 1}
                        icon={ChevronLeftIcon}
                        color="secondary"
                    />
                    <Text
                        variant="bodyMediumSize"
                    >
                        {localize("teachers-pagination-info", [
                            pagination.currentPage.toString(),
                            pagination.totalPages.toString()
                        ])}
                    </Text>
                    <Button
                        isDisabled={pagination.currentPage === pagination.totalPages}
                        onPress={() => handlePageChange(pagination.currentPage + 1)}
                        title={localize("teachers-pagination-next")}
                        icon={ChevronRightIcon}
                        iconDirection="right"
                        color="secondary"
                    />
                </View>)}
        </View>
        <Modal
            onOverlayPress={({
                closeAnimation
            }) => closeAnimation(() => setPreviewItem(null))}
            onClosed={() => setPreviewItem(null)}
            id="teacher-preview-modal"
            portalName="modal-system"
            isActive={!!previewItem}
        >
            <View
                style={[
                    stylesheet.modalContent,
                    {
                        backgroundColor: colors.content.container.default,
                        width: isMobile ? "100%" : 500,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {localize("teachers-preview-title")}
                </Text>
                <ScrollView
                    showsVerticalScrollIndicator={false}
                    style={{
                        flexShrink: 1
                    }}
                >
                    <View
                        style={[
                            stylesheet.formContainer,
                            {
                                marginTop: spaces.spacingMd,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <Text>{localize("teachers-preview-name", [
                            previewItem?.name_surname || ""
                        ])}</Text>
                        <Text>{localize("teachers-preview-tc", [
                            previewItem?.tc_no || ""
                        ])}</Text>
                        <Text>{localize("teachers-preview-age", [
                            previewItem?.age?.toString() || ""
                        ])}</Text>
                        <Text>{localize("teachers-preview-phone", [
                            previewItem?.phone || ""
                        ])}</Text>
                        <Text>{localize("teachers-preview-email", [
                            previewItem?.email || ""
                        ])}</Text>
                        <Text>{localize("teachers-preview-school", [
                            previewItem?.school_name || ""
                        ])}</Text>
                        <Text>{localize("teachers-preview-grade", [
                            previewItem?.grade_year || ""
                        ])}</Text>
                        <Text>{localize("teachers-preview-address", [
                            previewItem?.address || ""
                        ])}</Text>
                        <Text>{localize("teachers-preview-notes", [
                            previewItem?.notes || ""
                        ])}</Text>
                    </View>
                </ScrollView>
                <View
                    style={[
                        stylesheet.modalActions,
                        {
                            flexDirection: isMobile ? "column-reverse" : "row",
                            marginTop: spaces.spacingXl,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        title={localize("teachers-preview-close")}
                        onPress={() => setPreviewItem(null)}
                        spreadBehaviour="stretch"
                        type="neutral"
                    />
                </View>
            </View>
        </Modal>
        <Modal
            onOverlayPress={({
                closeAnimation
            }) => closeAnimation(() => {
                setModalVisible(false);
                setEditId(null);
            })}
            onClosed={() => {
                setModalVisible(false);
                setEditId(null);
            }}
            portalName="modal-system"
            isActive={modalVisible}
            id="teacher-modal"
        >
            <View
                style={[
                    stylesheet.modalContent,
                    {
                        backgroundColor: colors.content.container.default,
                        width: isMobile ? "100%" : 800,
                        borderRadius: radiuses.md,
                        padding: spaces.spacingXl
                    }
                ]}
            >
                <Text
                    variant="titleLargeSize"
                >
                    {editId ? localize("teachers-modal-edit-title") : localize("teachers-modal-create-title")}
                </Text>
                <ScrollView
                    showsVerticalScrollIndicator={false}
                    style={{
                        flexShrink: 1
                    }}
                >
                    <View
                        style={[
                            stylesheet.formContainer,
                            {
                                marginTop: spaces.spacingMd,
                                gap: spaces.spacingMd
                            }
                        ]}
                    >
                        <View
                            style={[
                                stylesheet.formRow,
                                {
                                    gap: isMobile ? spaces.spacingXl : spaces.spacingMd,
                                    flexDirection: isMobile ? "column" : "row"
                                }
                            ]}
                        >
                            <View
                                style={[
                                    stylesheet.formColumn,
                                    {
                                        flex: isMobile ? undefined : 1,
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <Text
                                    variant="titleMediumSize"
                                >
                                    {localize("teachers-modal-personal-info")}
                                </Text>
                                <TextInput
                                    placeholder={localize("teachers-modal-tc-placeholder")}
                                    title={localize("teachers-modal-tc-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        tc_no: text
                                    }))}
                                    defaultValue={formData.tc_no}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("teachers-modal-name-placeholder")}
                                    title={localize("teachers-modal-name-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        name_surname: text
                                    }))}
                                    defaultValue={formData.name_surname}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("teachers-modal-age-placeholder")}
                                    title={localize("teachers-modal-age-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        age: text
                                    }))}
                                    defaultValue={formData.age}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("teachers-modal-phone-placeholder")}
                                    title={localize("teachers-modal-phone-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        phone: text
                                    }))}
                                    defaultValue={formData.phone}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("teachers-modal-email-placeholder")}
                                    title={localize("teachers-modal-email-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        email: text
                                    }))}
                                    defaultValue={formData.email}
                                    keyboardType="email-address"
                                    spreadBehaviour="stretch"
                                    autoCapitalize="none"
                                />
                            </View>
                            <View
                                style={[
                                    stylesheet.formColumn,
                                    {
                                        flex: isMobile ? undefined : 1,
                                        gap: spaces.spacingMd
                                    }
                                ]}
                            >
                                <Text
                                    variant="titleMediumSize"
                                >
                                    {localize("teachers-modal-education-info")}
                                </Text>
                                <TextInput
                                    placeholder={localize("teachers-modal-school-placeholder")}
                                    title={localize("teachers-modal-school-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        school_name: text
                                    }))}
                                    defaultValue={formData.school_name}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("teachers-modal-grade-placeholder")}
                                    title={localize("teachers-modal-grade-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        grade_year: text
                                    }))}
                                    defaultValue={formData.grade_year}
                                    spreadBehaviour="stretch"
                                />
                                <TextInput
                                    placeholder={localize("teachers-modal-address-placeholder")}
                                    title={localize("teachers-modal-address-placeholder")}
                                    onChangeText={(text) => setFormData(prev => ({
                                        ...prev,
                                        address: text
                                    }))}
                                    defaultValue={formData.address}
                                    spreadBehaviour="stretch"
                                    multiline={true}
                                />
                            </View>
                        </View>
                        <TextInput
                            placeholder={localize("teachers-modal-notes-placeholder")}
                            title={localize("teachers-modal-notes-placeholder")}
                            onChangeText={(text) => setFormData(prev => ({
                                ...prev,
                                notes: text
                            }))}
                            defaultValue={formData.notes}
                            spreadBehaviour="stretch"
                            multiline={true}
                        />
                    </View>
                </ScrollView>
                <View
                    style={[
                        stylesheet.modalActions,
                        {
                            flexDirection: isMobile ? "column-reverse" : "row",
                            marginTop: spaces.spacingXl,
                            gap: spaces.spacingMd
                        }
                    ]}
                >
                    <Button
                        title={localize("teachers-modal-cancel")}
                        onPress={() => setModalVisible(false)}
                        spreadBehaviour="stretch"
                        type="neutral"
                    />
                    <Button
                        title={localize("teachers-modal-save")}
                        spreadBehaviour="stretch"
                        isLoading={isSubmitting}
                        onPress={handleSave}
                        type="primary"
                    />
                </View>
            </View>
        </Modal>
    </PageContainer>;
};

export default Teachers;
